import { HttpClient } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';

import { ReCaptchaV3Service } from 'ng-recaptcha';
import { firstValueFrom, map, switchMap, take } from 'rxjs';

import { HOTEL_SLUG } from '@valk-nx/core/lib/core';

export const NEWSLETTER_SUBSCRIBE_SERVICE_URL = new InjectionToken<string>(
	'newsletter-subscribe-service-url',
);

export const NEWSLETTER_API_COOKIES = new InjectionToken<string[]>(
	'newsletter_api-cookies',
);

@Injectable({ providedIn: 'root' })
export class NewsletterService {
	private readonly http = inject(HttpClient);
	private readonly subscribeUrl = inject(NEWSLETTER_SUBSCRIBE_SERVICE_URL, {
		optional: true,
	});
	private readonly apiCookies = inject<{
		withCredentials: boolean;
	}>(NEWSLETTER_API_COOKIES);
	private readonly hotelSlug = inject(HOTEL_SLUG);
	private readonly recaptchaV3Service = inject(ReCaptchaV3Service);

	newsletterSubscribe(
		emailAddress: string,
		recaptchaV2Token?: string,
	): Promise<
		| {
				success: boolean;
				status?: number;
		  }
		| { code: string; status?: number }
	> {
		if (recaptchaV2Token) {
			return this.recaptchaV2Submit(emailAddress, recaptchaV2Token);
		}
		return this.recaptchaV3Submit(emailAddress);
	}

	recaptchaV3Submit(emailAddress: string): Promise<{
		success: boolean;
		status?: number;
	}> {
		return firstValueFrom(
			this.recaptchaV3Service.execute('newsletterSubscribe').pipe(
				switchMap((token) => {
					return this.http.post<{ data: Record<string, never> }>(
						this.subscribeUrl,
						{
							emailAddress,
							hotelSlug: this.hotelSlug,
							token,
							recaptchaVersion: 'v3',
						},
						{
							...this.apiCookies,
							observe: 'body',
							responseType: 'json',
						},
					);
				}),
				take(1),
				map((body) => {
					return {
						success: body.data['success'],
					};
				}),
			),
		);
	}

	recaptchaV2Submit(
		emailAddress: string,
		recaptchaToken: string,
	): Promise<{
		success: boolean;
		status?: number;
	}> {
		return firstValueFrom(
			this.http
				.post<{ data: Record<string, never> }>(
					this.subscribeUrl,
					{
						emailAddress,
						hotelSlug: this.hotelSlug,
						token: recaptchaToken,
						recaptchaVersion: 'v2',
					},
					{
						...this.apiCookies,
						observe: 'body',
						responseType: 'json',
					},
				)
				.pipe(
					take(1),
					map((body) => {
						return {
							success: body.data['success'],
						};
					}),
				),
		);
	}
}
